/**
 * @file CustomerTasksTab.tsx
 * @description Вкладка «Задачи» карточки клиента (phase19.31, SC68): простой
 * список задач, звонков и встреч клиента — сначала открытые (с флажком
 * выполнения), ниже выполненные.
 *
 * Данные — `GET /admin/crm/activities?customerId&kinds=TASK,CALL,MEETING&status=…`
 * (SC60). После отметки список перечитывается и сообщает странице
 * (`onChanged`), чтобы обновились «Обзор» и счётчик вкладки.
 */

import { CheckCircle2, Plus, TriangleAlert } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { completeTaskWithUndo } from "../../../../components/crm/tasks/task-mutations";
import { taskAssigneeText } from "../../../../components/crm/tasks/task-texts";
import { Button } from "../../../../components/ui/button";
import { Checkbox } from "../../../../components/ui/checkbox";
import { ListError, ListSkeleton } from "../../../../components/ui/ListStates";
import { useNotify } from "../../../../components/ui/NotifyProvider";
import { SectionCard } from "../../../../components/ui/SectionCard";
import { ApiError } from "../../../../lib/api-client";
import { ACTIVITY_KIND_LABEL, listActivities, type ActivityDto, type ActivityKind } from "../../../../lib/api-crm";
import { formatShopDate } from "../../../../lib/shop-time";

const TASK_KINDS: ActivityKind[] = ["TASK", "CALL", "MEETING"];
/** Сколько выполненных показываем — свежие сверху у сервера по сроку, этого хватает для справки. */
const DONE_LIMIT = 20;

interface Props {
  customerId: string;
  canUseCrm: boolean;
  onAdd: () => void;
  /** Задача выполнена — странице перечитать сводку. */
  onChanged: () => void;
  /** Меняется после добавления задачи страницей — список перечитывается. */
  version: number;
}

export function CustomerTasksTab({ customerId, canUseCrm, onAdd, onChanged, version }: Props) {
  const notify = useNotify();
  const [open, setOpen] = useState<ActivityDto[] | null>(null);
  const [done, setDone] = useState<ActivityDto[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      listActivities({ customerId, kinds: TASK_KINDS, status: "open", limit: 100 }),
      listActivities({ customerId, kinds: TASK_KINDS, status: "done", limit: DONE_LIMIT }),
    ])
      .then(([o, d]) => {
        if (cancelled) return;
        setOpen(o.items);
        setDone(d.items);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Не удалось загрузить задачи клиента");
      });
    return () => {
      cancelled = true;
    };
  }, [customerId, attempt, version]);

  const complete = useCallback(
    (task: ActivityDto) => {
      setBusy(task.id);
      // Уведомление с «Отменить» — возврат задачи в работу (контракт §13).
      const reload = () => {
        setAttempt((n) => n + 1);
        onChanged();
      };
      void completeTaskWithUndo(task.id, { notify, reload }).finally(() => setBusy(null));
    },
    [notify, onChanged],
  );

  if (error && !open) return <ListError message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!open) return <ListSkeleton rows={3} compact />;

  return (
    <div className="flex flex-col gap-4">
      {canUseCrm ? (
        <Button variant="outline" className="self-start" onClick={onAdd}>
          <Plus className="size-4" aria-hidden />
          Задача
        </Button>
      ) : null}
      <SectionCard title={`Открытые · ${open.length}`}>
        {open.length === 0 ? (
          <p className="type-body text-on-surface-variant">Открытых задач нет</p>
        ) : (
          <ul aria-label="Открытые задачи" className="flex flex-col divide-y divide-[var(--border)]">
            {open.map((t) => (
              <li key={t.id}>
                <label className="flex min-h-12 cursor-pointer items-start gap-3 py-2">
                  {canUseCrm ? (
                    <Checkbox
                      className="mt-1"
                      checked={false}
                      disabled={busy === t.id}
                      onCheckedChange={(c) => c === true && complete(t)}
                    />
                  ) : null}
                  <TaskText task={t} />
                </label>
              </li>
            ))}
          </ul>
        )}
      </SectionCard>
      {done.length > 0 ? (
        <SectionCard title="Выполненные">
          <ul aria-label="Выполненные задачи" className="flex flex-col divide-y divide-[var(--border)]">
            {done.map((t) => (
              <li key={t.id} className="flex min-h-12 items-start gap-3 py-2">
                <CheckCircle2 aria-hidden className="mt-0.5 size-4 shrink-0 text-[var(--status-success-fg)]" />
                <TaskText task={t} done />
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : null}
    </div>
  );
}

/** Текст строки: заголовок, вид, срок или дата выполнения, сделка/заказ, исполнитель. */
function TaskText({ task, done = false }: { task: ActivityDto; done?: boolean }) {
  const overdue = !done && !!task.dueAt && new Date(task.dueAt).getTime() < Date.now();
  const when = done
    ? task.completedAt ? `выполнено ${formatShopDate(task.completedAt)}` : "выполнено"
    : task.dueAt ? `${overdue ? "просрочено · " : "до "}${formatShopDate(task.dueAt)}` : "без срока";
  const link = task.orderNumber ? `заказ ${task.orderNumber}` : task.dealTitle ? `сделка «${task.dealTitle}»` : null;
  return (
    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className={done ? "type-body break-words text-on-surface-variant" : "type-body break-words text-on-surface"}>{task.title}</span>
      <span className="type-caption flex flex-wrap items-center gap-x-2 text-on-surface-variant">
        <span>{ACTIVITY_KIND_LABEL[task.kind]}</span>
        <span className={overdue ? "inline-flex items-center gap-1 font-medium text-[var(--status-error-fg)]" : undefined}>
          {overdue ? <TriangleAlert aria-hidden className="size-3.5" /> : null}
          {when}
        </span>
        {link ? <span>{link}</span> : null}
        {/* Исполнитель, а не автор: кто должен сделать (crm.md T1). */}
        <span>{taskAssigneeText(task)}</span>
      </span>
    </span>
  );
}
